import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";
import FeatureGates from "@atlassian/feature-gate-js-client";

import Link from "@/components/wpl/link/src";
import { useLocalizedHref } from "@/libraries/web-framework";

import {
    StatsigExperimentCohort,
    StatsigExperimentKey
} from "../../../../../../feature-gates/types";

import { CtaButton } from "./components/cta-button";

export type headerProps = {
    readonly mainHeadingFirstPart: string;
    readonly mainHeadingSecondPart: string;
    readonly subHeading: string;
    readonly buttonText: string;
    readonly demoButtonText: string;
};

const sectionRootStyles = css({
    width: "100%",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start"
});

const sectionFramingStyles = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    justifyContent: "center",
    boxSizing: "border-box",
    marginBlockStart: "64px",
    [media.below.lg]: {
        marginBlockStart: "32px",
        width: "90%"
    }
});

const headingStylesFirstPart = css({
    color: "#101214",
    textAlign: "center",
    fontFamily: "var(--bxp-charlie-display-font-family)",
    fontSize: "66px",
    fontWeight: "700",
    lineHeight: "73px",
    [media.below.md]: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        fontSize: "36px",
        lineHeight: "50px"
    }
});

const spaceStyles = css({
    fontSize: "66px",
    fontWeight: "400",
    lineHeight: "73px"
});

const headingStylesSecondPart = css({
    color: "#101214",
    textAlign: "center",
    fontFamily: "var(--bxp-charlie-display-font-family)",
    fontSize: "66px",
    fontWeight: "400",
    lineHeight: "73px",
    [media.below.md]: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        fontSize: "36px",
        lineHeight: "50px"
    }
});

const productStyles = css({
    display: "grid",
    flexDirection: "column"
});

const headingStyles = css({
    textAlign: "center",
    [media.only.md]: {
        maxWidth: "847px"
    },
    [media.only.sm]: {
        maxWidth: "847px"
    }
});

const underlineContainerStyles = css({
    marginBlockStart: "-8px",
    justifySelf: "flex-end",
    [media.only.md]: {
        justifySelf: "center"
    },
    [media.below.md]: {
        marginBlockStart: "-16px"
    }
});

const underlineStyles = css({
    maxWidth: "93px",
    [media.below.md]: {
        maxWidth: "59px",
        marginInlineEnd: "18px"
    }
});

const subheadingStyles = css({
    fontFamily: "var(--bxp-charlie-text-font-family)",
    fontStyle: "normal",
    fontWeight: "400",
    fontSize: "20px",
    lineHeight: "28px",
    textAlign: "center",
    letterSpacing: "0",
    color: "#101214",
    [media.only.md]: {
        fontSize: "16px",
        lineHeight: "26px",
        boxSizing: "border-box"
    },
    [media.only.sm]: {
        fontSize: "16px",
        lineHeight: "26px",
        boxSizing: "border-box"
    },
    [media.below.sm]: {
        fontSize: "16px",
        lineHeight: "26px",
        boxSizing: "border-box"
    }
});

const ctaContainer = css({
    margin: "0rem 1rem"
});

const sandboxDemoCtaContainer = css({
    margin: "0rem 1rem",
    fontWeight: "500",
    [media.below.md]: {
        display: "none"
    }
});

const buttonGroupContainer = css({
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    marginBlockStart: "44px",
    [media.below.sm]: {
        marginBlockStart: "40px"
    }
});

const Header = ({
    mainHeadingFirstPart,
    mainHeadingSecondPart,
    subHeading,
    buttonText,
    demoButtonText
}: headerProps) => {
    const intl = useIntl();
    const englishOnly = intl.locale === "en";

    const paddingStyles = css({
        marginBlockStart: "15px"
    });

    const sandboxDemoButtonUrl = useLocalizedHref("/software/jira/sandbox");

    const sandboxM1: string = FeatureGates.getExperimentValue(
        StatsigExperimentKey.JFIND_SANDBOX_M1,
        "cohort",
        StatsigExperimentCohort.JFIND_SANDBOX_M1_CONTROL,
        { fireExperimentExposure: true }
    );

    return (
        <div css={sectionRootStyles}>
            <div css={sectionFramingStyles}>
                <div css={productStyles}>
                    <div css={headingStyles}>
                        <span css={headingStylesFirstPart}>
                            {mainHeadingFirstPart}
                        </span>
                        <span css={spaceStyles}> </span>
                        <span css={headingStylesSecondPart}>
                            {mainHeadingSecondPart}
                        </span>
                    </div>

                    <div css={underlineContainerStyles}>
                        {englishOnly && (
                            <img
                                css={underlineStyles}
                                src="https://wac-cdn.atlassian.com/misc-assets/webp-images/signal.webp"
                                alt=""
                            />
                        )}
                    </div>
                </div>
                <div css={[!englishOnly && paddingStyles, subheadingStyles]}>
                    {subHeading}
                </div>
                <div css={buttonGroupContainer}>
                    <div css={ctaContainer}>
                        <CtaButton
                            analyticsEventLabel="atlas-55799-header-cta-button-clicked"
                            text={buttonText}
                            ctaButtonUrl={useLocalizedHref(
                                "/try/cloud/signup?bundle=jira-software"
                            )}
                            bgColor="#0052CC"
                            textColor="#FFFFFF"
                        />
                    </div>
                    {sandboxM1 ===
                        StatsigExperimentCohort.JFIND_SANDBOX_M1_VARIANT && (
                        <div css={sandboxDemoCtaContainer}>
                            <Link
                                text={demoButtonText}
                                href={sandboxDemoButtonUrl}
                                type="basic-underline"
                                doesArrowAnimate={true}
                                dataEventProps={{
                                    "data-event-component": "linkButton",
                                    "data-event-container": "heroSection",
                                    "data-event": "clicked",
                                    "data-product-key": "jira",
                                    "data-label-english": "Try a demo",
                                    "data-label": demoButtonText
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export { Header };
